import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BackToDashboard } from "../components/BackToDashboard";
import { Check, Building2, Sparkles, CreditCard, User, Mail, Phone, X, Loader2, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import { validateEmail } from "../lib/validators";
import { Skeleton } from "../components/ui/skeleton";
import { loadPaddle } from "../lib/paddle";

const TABS = [
  { id: "profile",  label: "Profile",             icon: User       },
  { id: "billing",  label: "Plans & Billing",      icon: Sparkles   },
  { id: "manage",   label: "Manage Subscription",  icon: CreditCard },
];

// ── Inline OTP verification panel ─────────────────────────────────────────
function VerifyPanel({ field, newValue, onSuccess, onCancel, onChangeUsed }) {
  const [code, setCode]       = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent]       = useState(false);

  const sendCode = async () => {
    setSending(true);
    try {
      await api.post("/auth/request-change", { field, new_value: newValue });
      setSent(true);
      toast.success("Verification code sent to your current email.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send code.");
    } finally { setSending(false); }
  };

  const verify = async () => {
    if (code.length !== 6) { toast.error("Enter the 6-digit code."); return; }
    setVerifying(true);
    try {
      const r = await api.post("/auth/confirm-change", { field, code });
      // Count this as a successful change
      onChangeUsed();
      toast.success(r.data.message || "Updated successfully.");
      onSuccess(r.data.user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect or expired code.");
    } finally { setVerifying(false); }
  };

  return (
    <div className="mt-3 border border-zinc-200 bg-zinc-50 rounded-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
          Verify {field === "email" ? "Email" : "Phone"} Change
        </span>
        <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-700"><X size={14} /></button>
      </div>

      <p className="text-sm text-zinc-600">
        We'll send a 6-digit code to your <strong>current email</strong> to confirm changing your{" "}
        {field === "email" ? "email" : "phone number"} to{" "}
        <span className="font-mono font-semibold text-zinc-900">{newValue}</span>.
      </p>

      {!sent ? (
        <button
          onClick={sendCode}
          disabled={sending}
          className="flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50"
        >
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
          {sending ? "Sending…" : "Send verification code"}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
              className="ip flex-1 text-center font-mono text-lg tracking-widest"
              data-testid="otp-input"
            />
            <button
              onClick={verify}
              disabled={verifying || code.length !== 6}
              className="bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50"
              data-testid="otp-verify-button"
            >
              {verifying ? <Loader2 size={14} className="animate-spin" /> : "Confirm"}
            </button>
          </div>
          <button
            onClick={sendCode}
            disabled={sending}
            className="text-xs text-zinc-400 hover:text-zinc-600 underline underline-offset-2"
          >
            Resend code
          </button>
        </div>
      )}
    </div>
  );
}

// ── ChangePasswordPanel ──────────────────────────────────────────────────────
function ChangePasswordPanel({ onClose }) {
  const [step, setStep]             = useState(1); // 1=send code, 2=enter code+new pw
  const [sending, setSending]       = useState(false);
  const [code, setCode]             = useState("");
  const [newPw, setNewPw]           = useState("");
  const [confirmPw, setConfirmPw]   = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [verifying, setVerifying]   = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  function getStrength(pw) {
    if (!pw) return { score: 0, label: "", color: "" };
    let s = 0;
    if (pw.length >= 5)  s++;
    if (pw.length >= 10) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    if (s <= 1) return { score: s, label: "Weak",        color: "bg-red-500" };
    if (s === 2) return { score: s, label: "Fair",        color: "bg-orange-400" };
    if (s === 3) return { score: s, label: "Good",        color: "bg-yellow-400" };
    if (s === 4) return { score: s, label: "Strong",      color: "bg-green-500" };
    return              { score: s, label: "Very strong", color: "bg-emerald-500" };
  }

  const startCooldown = () => {
    setResendCooldown(60);
    const id = setInterval(() => setResendCooldown(c => { if (c <= 1) { clearInterval(id); return 0; } return c - 1; }), 1000);
  };

  const sendCode = async () => {
    setSending(true);
    try {
      await api.post("/auth/change-password/request-otp");
      toast.success("Verification code sent to your email.");
      setStep(2);
      startCooldown();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send code.");
    } finally { setSending(false); }
  };

  const verify = async () => {
    if (code.length !== 6)  { toast.error("Enter the 6-digit code."); return; }
    if (newPw.length < 5)   { toast.error("Password must be at least 8 characters."); return; }
    if (newPw !== confirmPw){ toast.error("Passwords do not match."); return; }
    setVerifying(true);
    try {
      await api.post("/auth/change-password/verify", { code, new_password: newPw });
      toast.success("Password changed. Please sign in again.");
      // Small delay so user sees the toast, then redirect to login
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect or expired code.");
    } finally { setVerifying(false); }
  };

  const { score, label, color } = getStrength(newPw);

  return (
    <div className="mt-3 border border-zinc-200 bg-zinc-50 rounded-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lock size={14} className="text-zinc-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Change Password</span>
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={14} /></button>
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            We'll send a 6-digit verification code to your current email address to confirm the change.
          </p>
          <button
            onClick={sendCode}
            disabled={sending}
            className="flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
            {sending ? "Sending…" : "Send verification code"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {/* Code input */}
          <div>
            <label className="label-eyebrow block mb-1.5">Verification code</label>
            <div className="flex gap-2">
              <input
                type="text" inputMode="numeric" maxLength={6}
                placeholder="6-digit code" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                className="ip flex-1 text-center font-mono text-lg tracking-widest"
                data-testid="change-pw-code"
              />
              <button
                onClick={() => { if (resendCooldown === 0) sendCode(); }}
                disabled={resendCooldown > 0 || sending}
                className="px-3 py-2 text-xs text-zinc-500 border border-zinc-200 rounded-sm hover:border-zinc-400 disabled:opacity-40 whitespace-nowrap"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend"}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="label-eyebrow block mb-1.5">New password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                placeholder="At least 8 characters"
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                className="ip pr-10"
                data-testid="change-pw-new"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                tabIndex={-1}
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {/* Strength meter */}
            {newPw && (
              <div className="mt-1.5 space-y-1">
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i < score ? color : "bg-zinc-200"}`} />
                  ))}
                </div>
                <p className={`text-xs font-medium ${score <= 1 ? "text-red-600" : score === 2 ? "text-orange-500" : score === 3 ? "text-yellow-600" : "text-green-600"}`}>{label}</p>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="label-eyebrow block mb-1.5">Confirm new password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                placeholder="Re-enter new password"
                value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)}
                style={{ borderColor: confirmPw && confirmPw !== newPw ? "#f87171" : confirmPw && confirmPw === newPw ? "#4ade80" : "" }}
                className="ip"
                data-testid="change-pw-confirm"
              />
            </div>
            {confirmPw && confirmPw !== newPw && (
              <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
            )}
            {confirmPw && confirmPw === newPw && (
              <p className="mt-1 text-xs text-green-600">Passwords match.</p>
            )}
          </div>

          <button
            onClick={verify}
            disabled={verifying || code.length !== 6 || newPw.length < 8 || newPw !== confirmPw}
            className="w-full bg-zinc-950 text-white py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="change-pw-submit"
          >
            {verifying ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            {verifying ? "Updating password…" : "Set new password"}
          </button>

          <p className="text-xs text-zinc-400">
            After changing your password, you'll be signed out and redirected to login.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function Account() {
  const { user: contextUser } = useOutletContext() || {};

  const [me, setMe]               = useState(null);
  const [form, setForm]           = useState({ name: "", company: "" });
  const [packages, setPackages]   = useState([]);
  const [plan, setPlan]           = useState(null);
  const [saving, setSaving]       = useState(false);
  const [subscribingId, setSubscribingId] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  // Pending change state — which field is currently awaiting verification
  const [showChangePw, setShowChangePw] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingPhone, setPendingPhone] = useState("");
  const [pendingPhoneDigits, setPendingPhoneDigits] = useState("");
  const [showVerify, setShowVerify]     = useState(null); // "email" | "phone" | null

  // Email change form state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail]           = useState("");
  const [confirmEmail, setConfirmEmail]   = useState("");
  const [emailFormError, setEmailFormError] = useState(null);

  const [loadError, setLoadError] = useState(false);
  const [loading,   setLoading]   = useState(true);

  // Seed user state from the context user already fetched by ProtectedShell —
  // this avoids a redundant /auth/me call on mount which was causing mobile
  // ITP/WebView cookie failures (the cookie was consumed by ProtectedShell's
  // request and not yet available for a second same-page request).
  const seedUser = (userData) => {
    setMe(userData);
    setForm({ name: userData.name || "", company: userData.company || "" });
    setPendingEmail(userData.email || "");
    const rawPhone = userData.phone || "";
    setPendingPhone(rawPhone);
    const digits = rawPhone.startsWith("+1") ? rawPhone.slice(2) : rawPhone.replace(/^\D/g, "");
    setPendingPhoneDigits(digits);
  };

  const loadBilling = async () => {
    try {
      const plansR = await api.get("/billing/packages");
      setPackages(plansR.data.packages || []);
    } catch { /* non-fatal — plans tab will show empty state */ }
    try {
      const planR = await api.get("/billing/plan");
      setPlan(planR.data);
    } catch { /* non-fatal — plan banner simply won't show */ }
  };

  const loadAll = async () => {
    setLoading(true);
    setLoadError(false);

    // ── Step 1: resolve user profile ──────────────────────────────────────
    // Use the user object already fetched by ProtectedShell (passed via Outlet
    // context) to avoid a duplicate /auth/me request. On mobile, making two
    // sequential authenticated requests in quick succession can fail because
    // ITP / WebView cookie jar delays mean the second request goes out before
    // the Set-Cookie from the first response has been committed, resulting in
    // a spurious 401 that shows the "couldn't load your account" error screen.
    if (contextUser && !me) {
      // Fast path: seed from already-authenticated context — no network request needed.
      seedUser(contextUser);
      await loadBilling();
      setLoading(false);
      return;
    }

    // Fallback (retry tap, or context missing): fetch fresh from the API.
    // Retry once with a short delay to handle transient mobile network issues.
    let meData = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const meR = await api.get("/auth/me");
        meData = meR.data;
        break;
      } catch (e) {
        if (attempt === 0) {
          await new Promise(r => setTimeout(r, 800));
        } else {
          setLoadError(true);
          setLoading(false);
          return;
        }
      }
    }

    seedUser(meData);
    await loadBilling();
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    const url = new URL(window.location.href);
    if (url.searchParams.get("billing") === "cancelled") {
      toast.info("Checkout cancelled — you can try again any time.");
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.pathname);
    }
    if (url.searchParams.get("tab") === "billing") setActiveTab("billing");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      // Only send name and company — email and password are never part of this form.
      // For Google OAuth users, these are the only two fields they can update.
      const payload = { name: form.name, company: form.company };
      const r = await api.patch("/auth/me", payload);
      seedUser(r.data);
      toast.success("Profile updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleChangeUsed = () => {
    // Rate limiting is enforced server-side; nothing to track locally
  };

  const handleVerifySuccess = (updatedUser) => {
    seedUser(updatedUser);
    setShowVerify(null);
    setShowEmailForm(false);
    setNewEmail("");
    setConfirmEmail("");
    setEmailFormError(null);
  };

  const subscribe = async (pkgId) => {
    setSubscribingId(pkgId);
    try {
      const r = await api.post("/billing/checkout", { package_id: pkgId, origin_url: window.location.origin });
      const { price_id, paddle_transaction_id, passthrough, customer_email, success_url } = r.data;
      const Paddle = await loadPaddle();
      if (!Paddle) {
        toast.error("Billing is not configured. Set REACT_APP_PADDLE_CLIENT_TOKEN.");
        setSubscribingId(null);
        return;
      }
      if (paddle_transaction_id) {
        Paddle.Checkout.open({
          transactionId: paddle_transaction_id,
          settings: { successUrl: success_url },
        });
      } else {
        Paddle.Checkout.open({
          items: [{ priceId: price_id, quantity: 1 }],
          customer: { email: customer_email },
          customData: passthrough,
          settings: { successUrl: success_url },
        });
      }
      setSubscribingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setSubscribingId(null);
    }
  };

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      const url = r.data.update_payment_method_url || r.data.cancel_url;
      if (!url) throw new Error("No portal URL returned");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setOpeningPortal(false);
    }
  };

  // ── Loading / error states ──────────────────────────────────────────────
  if (loading) return <AccountSkeleton />;
  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <Loader2 size={22} className="animate-spin text-zinc-400" />
      <p className="text-sm text-zinc-500">Loading your account…</p>
    </div>
  );

  if (loadError) return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 px-6 text-center">
      <div className="w-12 h-12 bg-zinc-100 rounded-sm flex items-center justify-center">
        <AlertCircle size={22} className="text-zinc-400" />
      </div>
      <div>
        <p className="font-semibold text-zinc-800">Couldn't load your account</p>
        <p className="text-sm text-zinc-500 mt-1 max-w-xs">
          This is usually a temporary network issue on mobile. Tap below to try again — no need to sign in again.
        </p>
      </div>
      <button
        onClick={loadAll}
        className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-800"
      >
        <Loader2 size={14} /> Retry
      </button>
    </div>
  );

  if (!me) return null;

  // Google OAuth users cannot change email or password — those are managed by Google
  const isGoogleUser = Boolean(me.is_google_user);

  // Rate limiting is enforced server-side. This variable keeps the UI consistent.
  const contactChangeLimitReached = false;

  const currentPlanId = plan?.plan || "free";
  const isPaid = currentPlanId !== "free";

  const handleChangeEmail = () => {
    if (isGoogleUser) return; // OAuth users cannot change email
    setShowEmailForm(true);
    setNewEmail("");
    setConfirmEmail("");
    setEmailFormError(null);
    setShowVerify(null);
  };

  const handleSubmitEmailForm = () => {
    setEmailFormError(null);
    const emailErr = validateEmail(newEmail);
    if (emailErr) { setEmailFormError(emailErr); return; }
    if (!confirmEmail) { setEmailFormError("Please confirm your new email address."); return; }
    if (newEmail.trim() !== confirmEmail.trim()) { setEmailFormError("Email addresses don't match. Please check and try again."); return; }
    if (newEmail.trim() === me.email) { setEmailFormError("New email is the same as your current email."); return; }
    setPendingEmail(newEmail.trim());
    setShowVerify("email");
  };

  const handleChangePhone = () => {
    if (isGoogleUser) return; // Belt-and-suspenders: OAuth users go through Google for account changes
    const fullPhone = pendingPhoneDigits ? `+1${pendingPhoneDigits}` : "";
    if (!fullPhone || fullPhone === me.phone) { toast.info("Enter a new phone number first."); return; }
    if (pendingPhoneDigits.length !== 10) { toast.error("Phone number must be exactly 10 digits."); return; }
    setPendingPhone(fullPhone);
    setShowVerify("phone");
  };

  return (
    <div className="px-4 sm:px-8 py-8 max-w-5xl mx-auto" data-testid="account-page">
      <BackToDashboard />
      <span className="label-eyebrow">Account</span>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-6">Account</h1>

      {/* Tab nav */}
      <div className="border-b border-zinc-200 mb-6 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  active ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"
                }`}>
                <Icon size={14} />{tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Profile tab ── */}
      {activeTab === "profile" && (
        <div className="space-y-4">

          {/* Name + Company */}
          <div className="border border-zinc-200 bg-white rounded-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              {me.picture ? (
                <img src={me.picture} alt="" className="w-12 h-12 rounded-sm" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-12 h-12 bg-zinc-100 rounded-sm flex items-center justify-center">
                  <Building2 size={20} className="text-zinc-400" />
                </div>
              )}
              <div>
                <div className="font-display font-bold tracking-tight">{me.name}</div>
                <div className="text-sm text-zinc-500">{me.email}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name">
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="ip" data-testid="account-name" />
              </Field>
              <Field label="Company">
                <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  className="ip" data-testid="account-company" />
              </Field>
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={save} disabled={saving} data-testid="save-account-button"
                className="bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50">
                {saving ? "Saving…" : "Save profile"}
              </button>
            </div>
          </div>

          {/* Email change */}
          <div className="border border-zinc-200 bg-white rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={15} className="text-zinc-400" />
              <span className="text-sm font-semibold text-zinc-800">Email address</span>
            </div>

            {/* Current email display */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-zinc-500 mb-0.5">Current email</div>
                <div className="text-sm font-mono text-zinc-800" data-testid="current-email-display">{me.email}</div>
              </div>
              {isGoogleUser ? null : (!showEmailForm && !showVerify && (
                <button
                  onClick={handleChangeEmail}
                  disabled={contactChangeLimitReached}
                  className="bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="change-email-button"
                >
                  Change email
                </button>
              ))}
            </div>

            {/* Google-managed email notice */}
            {isGoogleUser && (
              <div className="mt-4 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-sm p-4" data-testid="google-email-locked">
                <AlertCircle size={16} className="text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Email managed by Google</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    You signed in with Google, so your email address is controlled by your Google account. To change it,{" "}
                    <a href="https://myaccount.google.com/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 font-semibold hover:text-blue-900">
                      visit Google Account settings
                    </a>.
                  </p>
                </div>
              </div>
            )}

            {/* New email form — shown when Change email is clicked */}
            {!isGoogleUser && showEmailForm && !showVerify && (
              <div className="mt-4 border border-zinc-200 bg-zinc-50 rounded-sm p-4 space-y-3" data-testid="email-change-form">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Change Email Address</span>
                  <button
                    onClick={() => { setShowEmailForm(false); setNewEmail(""); setConfirmEmail(""); setEmailFormError(null); }}
                    className="text-zinc-400 hover:text-zinc-700"
                  >
                    <X size={14} />
                  </button>
                </div>

                <Field label="New email">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => { setNewEmail(e.target.value); setEmailFormError(null); }}
                    placeholder="you@newdomain.com"
                    className="ip"
                    data-testid="new-email-input"
                    autoComplete="off"
                  />
                </Field>

                <Field label="Confirm new email">
                  <input
                    type="email"
                    value={confirmEmail}
                    onChange={e => { setConfirmEmail(e.target.value); setEmailFormError(null); }}
                    placeholder="Repeat your new email"
                    className="ip"
                    data-testid="confirm-email-input"
                    autoComplete="off"
                    onPaste={e => e.preventDefault()}
                  />
                  <p className="text-xs text-zinc-400 mt-1">Type it in — paste is disabled to prevent copy errors.</p>
                </Field>

                {emailFormError && (
                  <div className="flex items-start gap-2 text-red-600 text-xs" data-testid="email-form-error">
                    <AlertCircle size={13} className="mt-0.5 shrink-0" />
                    {emailFormError}
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleSubmitEmailForm}
                    className="bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800"
                    data-testid="submit-email-form-button"
                  >
                    Continue
                  </button>
                  <button
                    onClick={() => { setShowEmailForm(false); setNewEmail(""); setConfirmEmail(""); setEmailFormError(null); }}
                    className="px-4 py-2 rounded-sm text-sm border border-zinc-200 bg-white hover:bg-zinc-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!isGoogleUser && showVerify === "email" && (
              <div className="mt-4">
                <VerifyPanel
                  field="email"
                  newValue={pendingEmail}
                  onSuccess={handleVerifySuccess}
                  onChangeUsed={handleChangeUsed}
                  onCancel={() => { setShowVerify(null); setShowEmailForm(false); setNewEmail(""); setConfirmEmail(""); }}
                />
              </div>
            )}
          </div>

          {/* Phone change */}
          <div className="border border-zinc-200 bg-white rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Phone size={15} className="text-zinc-400" />
              <span className="text-sm font-semibold text-zinc-800">Phone number</span>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Optional account contact number. US numbers only (+1).</p>
            <div className="flex gap-3 items-end">
              <Field label="Phone" className="flex-1">
                <div className="flex gap-2">
                  <div className="flex items-center px-3 py-2 border border-zinc-200 rounded-sm bg-zinc-50 text-sm text-zinc-500 font-medium select-none whitespace-nowrap">
                    +1
                  </div>
                  <input
                    type="tel"
                    value={pendingPhoneDigits}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setPendingPhoneDigits(digits);
                      setShowVerify(null);
                    }}
                    placeholder="5555550100"
                    maxLength={10}
                    className="ip flex-1"
                    data-testid="account-phone"
                  />
                </div>
              </Field>
              <button
                onClick={handleChangePhone}
                disabled={contactChangeLimitReached}
                className="bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800 whitespace-nowrap mb-px disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="change-phone-button"
              >
                Change phone
              </button>
            </div>
            {showVerify === "phone" && (
              <VerifyPanel
                field="phone"
                newValue={pendingPhone}
                onSuccess={handleVerifySuccess}
                onChangeUsed={handleChangeUsed}
                onCancel={() => { setShowVerify(null); const rawPhone = me.phone || ""; setPendingPhone(rawPhone); const d = rawPhone.startsWith("+1") ? rawPhone.slice(2) : rawPhone.replace(/^\+/, ""); setPendingPhoneDigits(d); }}
              />
            )}
          </div>

          {/* ── Change password — hidden for Google OAuth users ── */}
          <div className="border border-zinc-200 bg-white rounded-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock size={15} className="text-zinc-400" />
                <span className="text-sm font-semibold text-zinc-800">Password</span>
              </div>
              {!isGoogleUser && !showChangePw && (
                <button
                  onClick={() => setShowChangePw(true)}
                  className="text-sm font-semibold text-zinc-700 underline underline-offset-2 hover:text-zinc-950"
                  data-testid="change-password-button"
                >
                  Change password
                </button>
              )}
            </div>

            {isGoogleUser ? (
              <div className="mt-4 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-sm p-4" data-testid="google-password-locked">
                <AlertCircle size={16} className="text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Password managed by Google</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    You signed in with Google, so your password is controlled by your Google account. To change it,{" "}
                    <a href="https://myaccount.google.com/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 font-semibold hover:text-blue-900">
                      visit Google Account settings
                    </a>.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-500 mt-1">
                A verification code will be sent to your email address to confirm the change.
              </p>
            )}
            {!isGoogleUser && showChangePw && <ChangePasswordPanel onClose={() => setShowChangePw(false)} />}
          </div>
        </div>
      )}

      {/* ── Plans & Billing tab ── */}
      {activeTab === "billing" && (
        <div>
          {plan && (
            <div className="border border-zinc-200 bg-white rounded-sm p-5 mb-6 flex items-center justify-between" data-testid="current-plan-banner">
              <div>
                <span className="label-eyebrow">Current plan</span>
                <div className="font-display text-xl font-bold tracking-tight mt-1 flex items-center gap-2">
                  {plan.plan_name || "Free"}
                  {isPaid && <Sparkles size={16} className="text-amber-500" />}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {!isPaid
                    ? "Upgrade to unlock higher job limits, unlimited permits, and priority polling."
                    : `Renews ${plan.renews_at ? new Date(plan.renews_at).toLocaleDateString() : "automatically"} · $${plan.plan_amount?.toFixed(2)}/mo`}
                </div>
              </div>
              {isPaid && (
                <button onClick={() => setActiveTab("manage")}
                  className="text-sm font-semibold text-zinc-700 underline underline-offset-2 hover:text-zinc-950">
                  Manage subscription →
                </button>
              )}
            </div>
          )}

          {/* Plan limits reference table */}
          <div className="border border-zinc-200 bg-white rounded-sm p-5 mb-6" data-testid="plan-limits-table">
            <div className="label-eyebrow mb-3">Plan Limits At a Glance</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-zinc-100">
                    <th className="pb-2 pr-4 text-zinc-400 font-medium text-xs uppercase tracking-wider">Plan</th>
                    <th className="pb-2 pr-4 text-zinc-400 font-medium text-xs uppercase tracking-wider">Jobs</th>
                    <th className="pb-2 pr-4 text-zinc-400 font-medium text-xs uppercase tracking-wider">Permits / Job</th>
                    <th className="pb-2 text-zinc-400 font-medium text-xs uppercase tracking-wider">Alerts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {[
                    { name: "Free Trial", plan: "free",    jobs: "2",         permits: "5",         alerts: "Email only" },
                    { name: "Starter",    plan: "starter", jobs: "5",         permits: "20",        alerts: "Email" },
                    { name: "Pro",        plan: "pro",     jobs: "25",        permits: "Unlimited", alerts: "Email" },
                    { name: "Agency",     plan: "agency",  jobs: "Unlimited", permits: "Unlimited", alerts: "Email" },
                  ].map(row => (
                    <tr key={row.plan} className={currentPlanId === row.plan ? "bg-amber-50" : ""}>
                      <td className="py-2.5 pr-4">
                        <span className="font-semibold text-zinc-900">{row.name}</span>
                        {currentPlanId === row.plan && (
                          <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">
                            Current
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-zinc-700">{row.jobs}</td>
                      <td className="py-2.5 pr-4 text-zinc-700">{row.permits}</td>
                      <td className="py-2.5 text-zinc-700">{row.alerts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="plans-grid">
            {packages.map(p => {
              const active    = currentPlanId === p.id;
              const popular   = p.id === "pro";
              const featuresArr = (p.features || "").split(",").map(s => s.trim()).filter(Boolean);
              return (
                <div key={p.id}
                  className={`border rounded-sm p-5 bg-white relative flex flex-col ${active ? "border-zinc-950 ring-1 ring-zinc-950" : "border-zinc-200"}`}
                  data-testid={`plan-${p.id}`}>
                  {popular && (
                    <div className="absolute -top-2 left-4 bg-amber-500 text-zinc-950 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="label-eyebrow">{p.name}</div>
                  <div className="font-display text-3xl font-bold tracking-tight mt-2">
                    ${p.amount}<span className="text-sm text-zinc-500 font-normal">/mo</span>
                  </div>
                  <ul className="mt-4 space-y-1.5 flex-1">
                    {featuresArr.map(f => (
                      <li key={f} className="text-sm flex items-start gap-2">
                        <Check size={14} className="text-green-600 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => !active && subscribe(p.id)}
                    disabled={active || subscribingId === p.id}
                    data-testid={`subscribe-${p.id}`}
                    className={`mt-5 w-full py-2 rounded-sm text-sm font-semibold disabled:opacity-60 ${
                      active ? "bg-zinc-950 text-white" : "bg-amber-500 text-zinc-950 hover:bg-amber-400"
                    }`}>
                    {active ? "Current plan" : subscribingId === p.id ? "Opening checkout…" : `Subscribe — $${p.amount}/mo`}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-zinc-500">Payments processed securely by Paddle. All plans billed monthly.</p>
        </div>
      )}

      {/* ── Manage Subscription tab ── */}
      {activeTab === "manage" && (
        <div className="border border-zinc-200 bg-white rounded-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-zinc-100 rounded-sm flex items-center justify-center">
              <CreditCard size={18} className="text-zinc-500" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight">Manage your subscription</div>
              <div className="text-sm text-zinc-600">Update payment method, download invoices, or cancel your plan.</div>
            </div>
          </div>
          {plan && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 mb-6">
              <div className="text-xs text-zinc-500 mb-1">Active plan</div>
              <div className="font-semibold text-zinc-950">
                {plan.plan_name || "Free"}
                {isPaid && <span className="ml-2 text-xs text-zinc-500 font-normal">${plan.plan_amount?.toFixed(2)}/mo</span>}
              </div>
              {isPaid && plan.renews_at && (
                <div className="text-xs text-zinc-500 mt-1">
                  Next renewal: {new Date(plan.renews_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </div>
              )}
              {!isPaid && (
                <div className="text-xs text-zinc-500 mt-1">
                  No active paid subscription.{" "}
                  <button onClick={() => setActiveTab("billing")} className="underline underline-offset-2 text-zinc-700">View plans →</button>
                </div>
              )}
            </div>
          )}
          <div className="space-y-3">
            <div className="text-sm text-zinc-700 font-medium mb-2">Via the Paddle portal you can:</div>
            {["Update or change your payment method","Download past invoices and receipts","Switch between plans","Cancel your subscription"].map(item => (
              <div key={item} className="flex items-center gap-2 text-sm text-zinc-600">
                <Check size={13} className="text-green-600 flex-shrink-0" /> {item}
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-zinc-100">
            {isPaid ? (
              <button onClick={openPortal} disabled={openingPortal} data-testid="open-portal-button"
                className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50">
                <CreditCard size={14} />
                {openingPortal ? "Opening portal…" : "Open Paddle billing portal"}
              </button>
            ) : (
              <button onClick={() => setActiveTab("billing")}
                className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-amber-400">
                <Sparkles size={14} /> Choose a plan to get started
              </button>
            )}
            <p className="text-xs text-zinc-400 mt-3">You'll be redirected to Paddle's secure portal. Return to this page when done.</p>
          </div>
        </div>
      )}

      <style>{`.ip { width:100%; padding:8px 10px; border:1px solid #E4E4E7; border-radius:2px; font-size:14px; background:#fff; outline:none; } .ip:focus { border-color:#09090B; }`}</style>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

function AccountSkeleton() {
  return (
    <div className="px-4 sm:px-8 py-8 max-w-5xl mx-auto" data-testid="account-skeleton">
      <Skeleton className="h-4 w-28 mb-5" />
      <Skeleton className="h-4 w-24 mb-3" />
      <Skeleton className="h-10 w-48 mb-6" />
      <div className="border-b border-zinc-200 mb-6 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-40 mb-[-1px]" />)}
        </div>
      </div>
      <div className="space-y-4">
        <div className="border border-zinc-200 bg-white rounded-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <Skeleton className="w-12 h-12 rounded-sm" />
            <div>
              <Skeleton className="h-5 w-36 mb-2" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[...Array(2)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
          <Skeleton className="h-10 w-32 mt-5" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="border border-zinc-200 bg-white rounded-sm p-5">
              <Skeleton className="h-4 w-28 mb-3" />
              <Skeleton className="h-8 w-20 mb-4" />
              {[...Array(4)].map((__, j) => <Skeleton key={j} className="h-4 w-full mb-2" />)}
              <Skeleton className="h-10 w-full mt-5" />
            </div>
          ))}
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <Skeleton className="w-10 h-10 rounded-sm" />
            <div className="flex-1">
              <Skeleton className="h-5 w-48 mb-2" />
              <Skeleton className="h-4 w-full max-w-md" />
            </div>
          </div>
          <Skeleton className="h-20 w-full mb-5" />
          <Skeleton className="h-10 w-52" />
        </div>
      </div>
    </div>
  );
}
